/**
 * Xsolla integration types and constants
 */

/**
 * Purchase type for Xsolla transactions
 */
export enum XsollaPurchaseType {
  /** Recurring subscription purchase */
  SUBSCRIPTION = 'subscription',
  /** Non-recurring inventory or virtual item purchase */
  INVENTORY = 'inventory'
}

export const XSOLLA_ACTIVE_SUBSCRIPTION_ERROR_CODE = 'XSOLLA_ACTIVE_SUBSCRIPTION';

export const VALID_XSOLLA_SUBSCRIPTION_OPERATIONS = ['change_plan'] as const;
export type TXsollaSubscriptionOperation = (typeof VALID_XSOLLA_SUBSCRIPTION_OPERATIONS)[number];

/**
 * Languages Xsolla localizes Pay Station, receipts and plan copy into, as of 2026-06-09.
 * https://developers.xsolla.com/payment-ui-and-flow/payment-ui/localization/
 *
 * Sending a code outside this set risks a 422 on token creation and leaves plan copy on a
 * locale key Xsolla will never read, so both the checkout language and the copy sheet are
 * validated against it. Note `cn`/`tw` rather than ISO `zh-*`.
 */
export const VALID_XSOLLA_LOCALES = [
  'ar', 'bg', 'cn', 'cs', 'de', 'en', 'es', 'fr', 'he', 'id', 'it', 'ja', 'km', 'ko', 'lo', 'ms',
  'my', 'ne', 'ph', 'pl', 'pt', 'ro', 'ru', 'th', 'tr', 'tw', 'vi'
] as const;
export type TXsollaLocale = (typeof VALID_XSOLLA_LOCALES)[number];

export function isValidXsollaLocale(value: string): value is TXsollaLocale {
  return (VALID_XSOLLA_LOCALES as readonly string[]).includes(value);
}

export const VALID_XSOLLA_CHECKOUT_METHODS = ['alipay', 'wechat'] as const;
export type TXsollaCheckoutMethod = (typeof VALID_XSOLLA_CHECKOUT_METHODS)[number];

export const VALID_XSOLLA_CANCEL_REASON_CODES = [
  'too_expensive',
  'not_using_features',
  'missing_feature',
  'technical_or_payment_issue',
  'switching_provider',
  'temporary_pause',
  'other'
] as const;
export type TXsollaCancelReasonCode = (typeof VALID_XSOLLA_CANCEL_REASON_CODES)[number];

export type TXsollaCancelSubscriptionPayload = {
  reasonCode?: TXsollaCancelReasonCode;
  reasonText?: string;
};

export interface IXsollaPublicMarketConfig {
  publiclyEnabled: boolean;
  defaultForCountry: boolean;
  siteLocales: string[];
  availablePaymentMethods: TXsollaCheckoutMethod[];
}

export interface IXsollaPublicSiteConfig {
  enabled: boolean;
  sandbox: boolean;
  publicRolloutEnabled: boolean;
  authenticatedRolloutEligible: boolean;
  bypassMarketGating: boolean;
  markets: Record<string, IXsollaPublicMarketConfig>;
  resolvedCountry?: string | null;
  resolvedMarket?: string | null;
  checkoutEligible?: boolean;
  availablePaymentMethods?: TXsollaCheckoutMethod[];
}

export type TXsollaDisplayTier = 'revered' | 'exalted' | 'illustrious';

export interface IXsollaSubscriberAccountLink {
  redirectUrl: string;
}

/**
 * Xsolla tier configuration
 */
export type TXsollaTier = {
  name: string;
  productCode: string;
  monthlyPriceUsd: number;
  annualPriceUsd: number;
  ordinal: number;
};

/**
 * Xsolla subscription tiers - mirrors Patreon tiers
 * Only includes the 3 public-facing tiers (Revered, Exalted, Illustrious)
 */
export const XSOLLA_TIERS: Record<string, TXsollaTier> = {
  REVERED: {
    name: 'Revered',
    productCode: 'premium_3',
    monthlyPriceUsd: 5,
    annualPriceUsd: 50, // 2 months free
    ordinal: 3
  },
  EXALTED: {
    name: 'Exalted',
    productCode: 'premium_4',
    monthlyPriceUsd: 10,
    annualPriceUsd: 100, // 2 months free
    ordinal: 4
  },
  ILLUSTRIOUS: {
    name: 'Illustrious',
    productCode: 'premium_5',
    monthlyPriceUsd: 20,
    annualPriceUsd: 200, // 2 months free
    ordinal: 5
  }
};

/**
 * Xsolla webhook event types
 */
export type TXsollaWebhookType =
  | 'user_validation'
  | 'payment'
  | 'order_paid'
  | 'order_canceled'
  | 'refund'
  | 'create_subscription'
  | 'update_subscription'
  | 'cancel_subscription'
  | 'non_renewal_subscription'
  | 'afs_reject'
  | 'dispute';

/**
 * Base webhook payload structure
 */
export interface IXsollaWebhookBase {
  notification_type: TXsollaWebhookType;
  settings?: {
    project_id: number;
    merchant_id: number;
  };
}

type IXsollaWebhookUser = {
  id: string;
  email?: string;
  name?: string;
  public_id?: string;
  country?: string;
};

type IXsollaWebhookCustomParameters = {
  rio_user_id?: string;
  bnet_id?: string;
  source?: string;
  payment_external_id?: string;
  checkout_attempt_id?: string;
  ga_client_id?: string;
  ga_session_id?: string;
  subscription_operation?: string;
  [key: string]: string | undefined;
};

/**
 * User validation webhook payload
 */
export interface IXsollaUserValidationPayload extends IXsollaWebhookBase {
  notification_type: 'user_validation';
  user: IXsollaWebhookUser;
}

/**
 * Payment webhook payload.
 * Older projects can receive this separately from order webhooks.
 */
export interface IXsollaPaymentPayload extends IXsollaWebhookBase {
  notification_type: 'payment';
  user?: IXsollaWebhookUser;
  order?: {
    id?: number | string;
    external_id?: string;
    mode?: string;
    status?: string;
  };
  transaction?: {
    id?: number | string;
    external_id?: string;
    payment_method_order_id?: string;
  };
  payment_details?: {
    payment_method?: string;
  };
  custom_parameters?: IXsollaWebhookCustomParameters;
}

/**
 * Order paid webhook payload
 */
export interface IXsollaOrderPaidPayload extends IXsollaWebhookBase {
  notification_type: 'order_paid';
  order: {
    id: number;
    mode: string;
    status: string;
  };
  user: IXsollaWebhookUser;
  items?: IXsollaOrderItem[];
  subscription?: {
    subscription_id: number;
    plan_id: string;
    date_create: string;
    date_end?: string;
    date_next_charge?: string;
  };
  purchase?: {
    subscription?: {
      subscription_id: number;
      plan_id: string;
      date_create: string;
      date_end?: string;
      date_next_charge?: string;
    };
    checkout?: {
      currency: string;
      amount: number;
    };
    items?: IXsollaOrderItem[];
  };
  custom_parameters?: IXsollaWebhookCustomParameters;
}

/**
 * Subscription created webhook payload.
 * This is especially important for legacy subscription webhook flows and trial starts.
 */
export interface IXsollaCreateSubscriptionPayload extends IXsollaWebhookBase {
  notification_type: 'create_subscription';
  user: IXsollaWebhookUser;
  subscription: {
    subscription_id: number;
    plan_id: string;
    status?: string;
    date_create: string;
    date_end?: string;
    date_next_charge?: string;
  };
  custom_parameters?: IXsollaWebhookCustomParameters;
}

/**
 * Subscription update webhook payload
 */
export interface IXsollaSubscriptionUpdatePayload extends IXsollaWebhookBase {
  notification_type: 'update_subscription';
  user: IXsollaWebhookUser;
  subscription: {
    subscription_id: number;
    plan_id: string;
    status?: string;
    date_create?: string;
    date_end?: string;
    date_next_charge?: string;
  };
  custom_parameters?: IXsollaWebhookCustomParameters;
}

/**
 * Subscription cancel webhook payload
 */
export interface IXsollaSubscriptionCancelPayload extends IXsollaWebhookBase {
  notification_type: 'cancel_subscription';
  user: IXsollaWebhookUser;
  subscription: {
    subscription_id: number;
    plan_id: string;
    date_create: string;
    date_end: string;
  };
  custom_parameters?: IXsollaWebhookCustomParameters;
}

/**
 * Non-renewal subscription webhook payload (user won't be charged again)
 */
export interface IXsollaNonRenewalPayload extends IXsollaWebhookBase {
  notification_type: 'non_renewal_subscription';
  user: IXsollaWebhookUser;
  subscription: {
    subscription_id: number;
    plan_id: string;
    date_create: string;
    date_end: string;
  };
  custom_parameters?: IXsollaWebhookCustomParameters;
}

/**
 * Order canceled webhook payload
 */
export interface IXsollaOrderCanceledPayload extends IXsollaWebhookBase {
  notification_type: 'order_canceled';
  order: {
    id: number;
    status: string;
  };
  user: IXsollaWebhookUser;
  subscription?: {
    subscription_id: number;
  };
}

/**
 * Refund webhook payload
 */
export interface IXsollaRefundPayload extends IXsollaWebhookBase {
  notification_type: 'refund';
  user: IXsollaWebhookUser;
  transaction: {
    id: number;
    external_id?: string;
    payment_method_order_id?: string;
  };
  purchase?: {
    subscription?: {
      subscription_id: number;
      plan_id: string;
      date_create?: string;
      date_end?: string;
    };
    items?: IXsollaOrderItem[];
  };
  custom_parameters?: IXsollaWebhookCustomParameters;
}

/**
 * Optional anti-fraud rejection webhook payload.
 */
export interface IXsollaAfsRejectPayload extends IXsollaWebhookBase {
  notification_type: 'afs_reject';
  user?: IXsollaWebhookUser;
  transaction?: {
    id?: number | string;
    external_id?: string;
    payment_method_order_id?: string;
  };
  purchase?: {
    subscription?: {
      subscription_id?: number;
      plan_id?: string;
      date_create?: string;
      date_end?: string;
    };
    items?: IXsollaOrderItem[];
  };
  custom_parameters?: IXsollaWebhookCustomParameters;
}

/**
 * Optional chargeback/dispute webhook payload.
 */
export interface IXsollaDisputePayload extends IXsollaWebhookBase {
  notification_type: 'dispute';
  user?: IXsollaWebhookUser;
  transaction?: {
    id?: number | string;
    external_id?: string;
    payment_method_order_id?: string;
  };
  payment_details?: {
    payment_method?: string;
  };
  custom_parameters?: IXsollaWebhookCustomParameters;
  dispute?: {
    status?: string;
    reason?: string;
    amount?: number | string;
    currency?: string;
  };
}

/**
 * Minimal Xsolla order item payload used for one-time inventory purchases.
 */
export interface IXsollaOrderItem {
  sku: string;
  name?: string;
  quantity?: number;
  amount?: number;
  is_free?: boolean;
}

/**
 * Union type for all webhook payloads
 */
export type TXsollaWebhookPayload =
  | IXsollaUserValidationPayload
  | IXsollaPaymentPayload
  | IXsollaOrderPaidPayload
  | IXsollaCreateSubscriptionPayload
  | IXsollaSubscriptionUpdatePayload
  | IXsollaSubscriptionCancelPayload
  | IXsollaNonRenewalPayload
  | IXsollaRefundPayload
  | IXsollaOrderCanceledPayload
  | IXsollaAfsRejectPayload
  | IXsollaDisputePayload;

/**
 * Xsolla API subscription response
 */
export interface IXsollaSubscription {
  id: number;
  plan_id: string;
  plan_name?: string;
  status: 'active' | 'non_renewing' | 'canceled';
  date_create: string;
  date_end: string;
  date_next_charge?: string;
  charge?: {
    amount: number;
    currency: string;
  };
}

/**
 * Token creation response
 */
export interface IXsollaTokenResponse {
  token: string;
  orderId?: string;
  paymentExternalId?: string;
}

export interface IXsollaDisplayPrice {
  amount: number;
  currency: string;
  baseAmount: number;
  baseCurrency: string;
  isLocalized: boolean;
}

export interface IXsollaDisplayTierPricing {
  monthly: IXsollaDisplayPrice;
  annual: IXsollaDisplayPrice;
}

export interface IXsollaPricingResponse {
  countryCode: string | null;
  preferredCurrency: string | null;
  hasLocalizedPricing: boolean;
  plans: Record<TXsollaDisplayTier, IXsollaDisplayTierPricing>;
}

/**
 * Sync result
 */
export interface IXsollaSyncResult {
  created: number;
  updated: number;
  disabled: number;
  errors: string[];
}

/**
 * Webhook response for user validation
 */
export interface IXsollaWebhookResponse {
  success?: boolean;
  user?: {
    id: string;
    email?: string;
    name?: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

/**
 * Format transaction ID for Xsolla subscriptions
 */
export function formatXsollaTransactionId(subscriptionId: number | string): string {
  return `xsolla-${subscriptionId}`;
}

export function parseXsollaTransactionId(transactionId: string): string | null {
  const match = /^xsolla-(.+)$/.exec(transactionId || '');
  return match?.[1] || null;
}

/**
 * Build the Xsolla user ID for a RaiderIO user.
 *
 * RaiderIO user IDs never change, so this identity is stable for the lifetime
 * of the account. For users who restored purchases from a deleted account, the
 * token-creation path reuses the existing XsollaAccount.xsolla_user_id instead
 * of calling this — so Xsolla-side continuity (saved payment methods,
 * subscription history) is preserved across the restore.
 */
export function buildXsollaUserId(userId: number): string {
  return `rio_${userId}`;
}
